import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { useAdminAuth } from '../adminAuth';

interface InitResponse {
  otpauth_uri: string;
  qr_data_url: string;
  secret_b32: string;
}

export function Admin2FASetup() {
  const navigate = useNavigate();
  const { refresh } = useAdminAuth();
  const [enrollment, setEnrollment] = useState<InitResponse | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Guard against React StrictMode double-mount. Pending row is upserted
  // idempotently server-side, but a second init re-rolls the secret;
  // we'd rather not flash a new QR mid-render.
  const initFired = useRef(false);

  useEffect(() => {
    if (initFired.current) return;
    initFired.current = true;
    void (async () => {
      try {
        const res = await adminApi<InitResponse>('/v1/auth/admin/2fa/setup-init', {
          method: 'POST',
        });
        setEnrollment(res);
      } catch (err) {
        const e = err as { status?: number; body?: { message?: string } };
        if (e.status === 401) {
          navigate('/admin/login', { replace: true });
        } else if (e.status === 409) {
          // Already enrolled — go straight to verify.
          navigate('/admin/verify-2fa', { replace: true });
        } else {
          setError(e.body?.message ?? 'Failed to start enrollment.');
        }
      }
    })();
  }, [navigate]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await adminApi('/v1/auth/admin/2fa/setup-confirm', {
        method: 'POST',
        body: { code },
      });
      await refresh();
      navigate('/admin', { replace: true });
    } catch (err) {
      const e = err as { status?: number; body?: { message?: string } };
      setError(e.body?.message ?? 'Invalid code.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="admin-auth-shell">
      <div className="admin-auth-card admin-auth-card-wide">
        <h1>Set up two-factor authentication</h1>
        <p className="admin-auth-sub">
          Scan the QR code below with Google Authenticator, 1Password, or Authy.
        </p>
        {enrollment === null ? (
          <p>{error ?? 'Loading…'}</p>
        ) : (
          <>
            <div className="admin-qr-wrap">
              <img src={enrollment.qr_data_url} alt="TOTP QR code" />
            </div>
            <p className="admin-secret-fallback">
              Can&apos;t scan? Enter this secret manually:
              <code data-testid="totp-secret-b32">{enrollment.secret_b32}</code>
            </p>
            <form onSubmit={onSubmit}>
              {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}
              <label htmlFor="admin-totp-code">6-digit code</label>
              <input
                id="admin-totp-code"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
              />
              <button type="submit" className="admin-primary-button" disabled={submitting}>
                {submitting ? 'Verifying…' : 'Confirm and sign in'}
              </button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
